import { forEach } from 'lodash';
import getSymbolFromCurrency from 'currency-symbol-map';
import moment from 'moment-timezone';
import i18next from 'i18next';
import { HOURS_OPTIONS } from '../constants/AppConstants';
import { visiblePhoneNumber } from './phoneNumber';
import { formatMoney } from './numberUtils';

export const customerDisplayName = (customer = {}) => {
  let fullName = customer?.full_name || customer?.full_names || '';

  if (!fullName.trim()) {
    fullName = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
  }
  if (!fullName.trim()) { fullName = customer?.username || ''; }
  if (!fullName.trim()) { fullName = customer?.whatsapp_name || ''; }

  return fullName;
};

export const customerFullName = (customer = {}, currentRetailerUser = {}) => {
  let fullName = customerDisplayName(customer);

  if (!fullName.trim()) { fullName = visiblePhoneNumber(customer?.phone, currentRetailerUser); }
  if (!fullName.trim()) { fullName = customer?.email || ''; }

  return fullName;
};

export const getCaretCharacterOffsetWithin = (element) => {
  let caretOffset = 0;
  const doc = element.ownerDocument || element.document;
  const win = doc.defaultView || doc.parentWindow;
  let sel;
  if (typeof win.getSelection !== "undefined") {
    sel = win.getSelection();
    if (sel.rangeCount > 0) {
      const range = win.getSelection().getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(element);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      caretOffset = preCaretRange.toString().length;
    }
  } else if ((sel = doc.selection) && sel.type !== "Control") {
    const textRange = sel.createRange();
    const preCaretTextRange = doc.body.createTextRange();
    preCaretTextRange.moveToElementText(element);
    preCaretTextRange.setEndPoint("EndToEnd", textRange);
    caretOffset = preCaretTextRange.text.length;
  }
  return caretOffset;
};

export const insertTextAtCaret = (text) => {
  let sel; let range;
  if (window.getSelection) {
    sel = window.getSelection();
    if (sel.getRangeAt && sel.rangeCount) {
      range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
    }
  } else if (document.selection && document.selection.createRange) {
    document.selection.createRange().text = text;
  }
};

// eslint-disable-next-line consistent-return
export const getFileType = (file) => {
  if (!file) return '';

  // eslint-disable-next-line default-case
  switch (file.type) {
    case 'image/jpg':
    case 'image/jpeg':
    case 'image/png':
      return 'image';
    case 'application/pdf':
      return 'pdf';
  }
};

export const formatOrderNoteMessage = (order, retailer, platform = '', withPaymentMethods = false) => {
  const t = i18next.t.bind(i18next);
  const symbol = getSymbolFromCurrency(order.currency || retailer.currency_code || 'USD');
  const code = order.currency || retailer.currency_code || 'USD';
  const sym = platform === 'whatsapp' ? '*' : '';

  let orderString = `${sym}${t('Inbox:orderHowMessage.title')}${sym}\n\n${sym}Productos:${sym}\n`;

  const products = order.products || order.order_items || [];

  forEach(products, (product) => {
    // Para las variantes antiguas - en un futuro eliminar esta parte
    let text = getFormatVariantsHowText(product.variants || []);
    if (text) text = `(${text})`;

    // Para las nuevas variantes porque se guardan en el nuevo campo variant_name
    if (!text && product.variant_name) text = `(${product.variant_name})`;

    orderString += `${sym}-${sym} ${product?.title} ${text}  x  ${product?.quantity}\n`;
  });

  if (order.delivery_method != null) {
    orderString += `\n${sym}Entrega${sym}\n`;

    if (order.delivery_method === 'store_pickup') {
      orderString += `Recoger en tienda\n`;
    } else {
      orderString += `Envío a domicilio\n`;
      orderString += `Dirección: ${order?.address}\n`;
      orderString += `${order?.city}\n`;
      orderString += `${order?.state}\n`;
      orderString += `${order?.zip_code}\n`;
    }
  }

  orderString += `${sym}Resumen${sym}\n`;
  orderString += `Subtotal: ${symbol}${formatMoney(order?.subtotal)} ${code}\n`;

  if (Number(order?.total_discount) > 0) {
    orderString += `Descuento: -${symbol}${formatMoney(order?.total_discount)} ${code} ${order?.discount_reason ? `(${order?.discount_reason})` : ''}\n`;
    orderString += `Subtotal con descuento: ${symbol}${formatMoney(order?.subtotal_with_discount)} ${code}\n`;
  }

  const tax = order?.tax || order?.tax_amount || 0;

  if (Number(tax) > 0) {
    orderString += `Impuesto: ${symbol}${formatMoney(tax)} ${code}\n`;
  }

  if (Number(order?.shipping_value) > 0) {
    orderString += `Valor de envío: ${symbol}${formatMoney(order?.shipping_value)} ${code}\n`;
  }

  orderString += `${sym}Total${sym}: ${symbol}${formatMoney(order?.total)} ${code}\n`;

  if (withPaymentMethods) {
    orderString += `\n${sym}Métodos de pago disponibles${sym}`;
  }

  return orderString;
};

export const formatSimpleOrderMessage = (order, platform = '', currency_symbol = '$', currency = 'USD') => {
  const t = i18next.t.bind(i18next);
  const sym = platform === 'whatsapp' ? '*' : '';
  let text = '';

  if (order.description) {
    text = `${sym}${t('Inbox:sendPaymentLinks.orderDetailTitle')}${sym}\n\n${order.description}\n`;
  }
  if (order.total || order.amount) {
    const amount = order.total || order.amount || 0;
    const cleanAmount = String(amount).replace(/,/g, '').replace(/[^\d.-]/g, '');
    const amountNumber = cleanAmount ? Number(cleanAmount) : 0;
    text += text ? `\n${sym}${t('Inbox:sendPaymentLinks.total')}${sym}: ${currency_symbol}${formatMoney(amountNumber)} ${currency}\n` : `${sym}${t('Inbox:sendPaymentLinks.total')}${sym}: ${currency_symbol}${formatMoney(amountNumber)} ${currency}\n`;
  }

  return text;
};

export const formatDate = (retailer = {}, date = moment(), format = 'DD-MM-YYYY', locale) => {
  const timeZone = retailer.timezone || 'America/Guayaquil';
  const momentDate = moment(date).tz(timeZone);

  return locale ? momentDate.locale(locale).format(format) : momentDate.format(format);
};

export const strPadStart = (number = 1, length = 6, mask = '0') => number.toString().padStart(length, mask);

export const daysWithHours = () => {
  let array = [];

  // eslint-disable-next-line no-plusplus
  for (let day = 1; day <= 7; day++) {
    // eslint-disable-next-line no-plusplus
    for (let hour = 0; hour < 24; hour++) {
      array = [
        ...array,
        { day, hour: `${hour.toString().padStart(2, '0')}`, available: true }
      ];
    }
  }

  return array;
};

export const getVariantsHowText = (variants) => getFormatVariantsHowText(variants);

export const pluck = (arr, key) => arr.map((i) => i[key]);

export const validateEmailValue = (email) => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const formatTimeOption = (time) => HOURS_OPTIONS.find((option) => option.value === time);

const getFormatVariantsHowText = (variants) => {
  let text = '';

  forEach(variants, (variant, index) => {
    if (index > 0) text += '/';

    text += `${variant.variant_option_name}`;
  });

  if (text) return `${text}`;

  return '';
};

export const uuid = () => new Date().getTime().toString(36) + Math.random().toString(36).slice(2);

export const geocoderLocationInfo = (results) => {
  const location = {
    address: results[0].formatted_address,
    city: '',
    state: '',
    country: '',
    country_id: '',
    zip_code: '',
    latitude: results[0].geometry.location.lat(),
    longitude: results[0].geometry.location.lng()
  };

  results[0].address_components.forEach((component) => {
    const { types } = component;

    if (types.includes('locality')) {
      location.city = component.long_name;
    }

    if (types.includes('administrative_area_level_1')) {
      location.state = component.long_name;
    }

    if (types.includes('country')) {
      location.country = component.long_name;
      location.country_id = component.short_name;
    }

    if (types.includes('postal_code')) {
      location.zip_code = component.long_name;
    }
  });

  return location;
};

/**
 * Extracts up to two uppercase letters from a given word.
 * If there are fewer than two uppercase letters, it returns the first two letters of the word in uppercase.
 * If the input word is empty or undefined, it returns an empty string.
 *
 * @param {string} word - The input word from which to extract uppercase letters.
 * @returns {string} A string containing up to two uppercase letters.
 *
 * @example
 * console.log(twoUpperCaseLetters("Mercately"));  // Output: "ME"
 * console.log(twoUpperCaseLetters("mErCaTeLy"));  // Output: "EC"
 * console.log(twoUpperCaseLetters("12345"));      // Output: "12"
 * console.log(twoUpperCaseLetters(""));           // Output: ""
 */
export const twoUpperCaseLetters = (word) => {
  if (!word || word.trim() === '') {
    return '';
  }

  // eslint-disable-next-line no-shadow
  const extractUpperCaseLetters = (word) => word.split('').filter((char) => char === char.toUpperCase() && isNaN(char));

  const upperCaseLetters = extractUpperCaseLetters(word).slice(0, 2).join('');

  if (upperCaseLetters) {
    return upperCaseLetters;
  }

  return word.slice(0, 2).toUpperCase();
};

export const isValidInputValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

export const formatAvatarName = (firstName, lastName) => {
  const chart1 = firstName ? firstName.trim() : '';
  const chart2 = lastName ? lastName.trim() : '';

  let initials = chart1 ? `${chart1.charAt(0).toUpperCase()}` : '';
  initials += chart2 ? `${chart2.charAt(0).toUpperCase()}` : '';

  return initials;
};
